import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Send, Loader2, HeartHandshake, Globe } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Seo } from "../components/Seo";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message envoyé ! Nous vous répondrons rapidement.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 pb-24 max-w-7xl mx-auto px-6 lg:px-10 min-h-screen" data-testid="contact-page">
      <Seo title="Contact" description="Contactez La Fondation des Artistes Handicapées de France : questions, commandes, partenariats. Soutenez aussi notre cagnotte." path="/contact" />
      <div className="grid lg:grid-cols-2 gap-16">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#60a5fa] mb-5">Contact</p>
          <h1 className="font-display text-5xl md:text-6xl text-white tracking-tight leading-tight mb-8">
            Parlons d'art ensemble.
          </h1>
          <p className="text-zinc-300 leading-relaxed mb-12 max-w-md">
            Une question sur une œuvre, une commande spéciale, un partenariat ou une demande d'artiste ?
            Écrivez-nous, nous vous répondrons avec plaisir.
          </p>
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Mail className="h-5 w-5 text-[#60a5fa]" />
              <span className="text-zinc-300">lafondationdesartisteshandicap@yahoo.com</span>
            </div>
            <div className="flex items-center gap-4">
              <MapPin className="h-5 w-5 text-[#60a5fa]" />
              <span className="text-zinc-300">Paris, France</span>
            </div>
            <a
              href="https://www.leetchi.com/fr/c/la-fondation-des-artistes-handicapes-1454665"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-leetchi-link"
              className="flex items-center gap-4 text-zinc-300 hover:text-white transition-colors group"
            >
              <HeartHandshake className="h-5 w-5 text-[#E11D48]" />
              <span className="group-hover:underline">Soutenir la cagnotte Leetchi</span>
            </a>
            <a
              href="https://la-fondation-des-artistes-handicaps.lovable.app/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-website-link"
              className="flex items-center gap-4 text-zinc-300 hover:text-white transition-colors group"
            >
              <Globe className="h-5 w-5 text-[#60a5fa]" />
              <span className="group-hover:underline">Site de La Fondation des Artistes Handicapées</span>
            </a>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
          <input
            data-testid="contact-name"
            value={form.name}
            onChange={update("name")}
            placeholder="Votre nom *"
            className="w-full bg-[#0d0d0d] border border-white/15 px-4 py-3 text-white focus:border-[#2563EB] outline-none"
          />
          <input
            data-testid="contact-email"
            type="email"
            value={form.email}
            onChange={update("email")}
            placeholder="Votre email *"
            className="w-full bg-[#0d0d0d] border border-white/15 px-4 py-3 text-white focus:border-[#2563EB] outline-none"
          />
          <input
            data-testid="contact-subject"
            value={form.subject}
            onChange={update("subject")}
            placeholder="Sujet"
            className="w-full bg-[#0d0d0d] border border-white/15 px-4 py-3 text-white focus:border-[#2563EB] outline-none"
          />
          <textarea
            data-testid="contact-message"
            value={form.message}
            onChange={update("message")}
            placeholder="Votre message *"
            rows={6}
            className="w-full bg-[#0d0d0d] border border-white/15 px-4 py-3 text-white focus:border-[#2563EB] outline-none resize-none"
          />
          <button
            data-testid="contact-submit"
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-3 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white px-10 py-4 text-sm tracking-wide transition-colors"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Envoyer le message
          </button>
        </form>
      </div>
    </div>
  );
}
